/**
 * La progression depuis septembre, sans confondre progrès et hasard (11/10/2026, À L'ESSAI à côté des vues
 * actuelles, qui restent celles par défaut ; le prof décidera s'il faut remplacer).
 *
 * Deux passations ne donnent jamais les mêmes taux : à un QCM à quatre choix on devine juste une fois sur quatre,
 * un élève fatigué rate ce qu'il sait, une variante n'a pas tout à fait la difficulté de l'item de septembre. La
 * MARGE DU HASARD dit jusqu'où un écart peut aller par simple hasard (deux erreurs types, 95 cas sur 100) : on ne
 * parle de progrès ou de recul qu'au-delà. Sur une question et 26 élèves, elle vaut environ 27 points ; sur un
 * domaine de 20 questions, environ 6.
 *
 * Épreuve de Musk du 12/10/2026 : la marge reste un calcul interne, elle ne s'affiche plus. Chaque item de
 * septembre est un savoir (une variante garde l'identité de son item). L'écran d'un QCM de suivi montre la classe
 * par domaine, la moyenne d'avant puis celle de maintenant (bilanDeLaClasse, comprehensionDeLaClasse), puis chaque
 * élève, du plus grand progrès au plus grand recul, ses questions d'un clic (elevesDuQcm) ; « Depuis septembre »
 * montre, pour chaque question repassée, qui l'a acquise et qui doit la revoir (questionsRepassees).
 *
 * Les copies : septembre, eleve.reponses[nom du test][Qnn] = { score } (Automatismes, Problèmes, Lexique,
 * Compétence, ou le nom court d'un texte) ; QCM de suivi, eleve.reponses[type_label][id de la question] =
 * { score, question_num }. Une question sans réponse rangée ne compte pas (comme progressionParCompetence).
 */
import { nomDuTexte } from '../EvalNat/evaluationConfig';
import { COMPETENCES_ORDRE, formeDuBilan, progressionComprehension } from './progressionParCompetence';

const sansApostropheCourbe = (s) => String(s || '').replace(/’/g, "'");
const numero = (questionNum) => parseInt(String(questionNum ?? '').replace(/\D/g, ''), 10);
const estJuste = (score) => score === 1 || score === '1';

/** La marge du hasard sur l'écart de deux taux, en points ; chaque taux lissé d'une réussite et d'un échec. */
export const margeDuHasard = (justes1, total1, justes2, total2) => {
    if (!total1 || !total2) return null;
    const p1 = (justes1 + 1) / (total1 + 2);
    const p2 = (justes2 + 1) / (total2 + 2);
    return 196 * Math.sqrt((p1 * (1 - p1)) / total1 + (p2 * (1 - p2)) / total2);
};

/** 'progres', 'recul' ou 'stable' (l'écart tient dans la marge) ; null sans marge. */
export const verdictDeLEcart = (ecart, marge) => {
    if (marge === null || marge === undefined) return null;
    if (ecart > marge) return 'progres';
    if (ecart < -marge) return 'recul';
    return 'stable';
};

/** Deux comptes { justes, total } : les taux, l'écart en points, la marge et le verdict. */
export const comparerComptes = (avant, apres) => {
    if (!avant?.total || !apres?.total) return null;
    const tauxAvant = (100 * avant.justes) / avant.total;
    const tauxApres = (100 * apres.justes) / apres.total;
    const ecart = tauxApres - tauxAvant;
    const marge = margeDuHasard(avant.justes, avant.total, apres.justes, apres.total);
    return { avant, apres, tauxAvant, tauxApres, ecart, marge, verdict: verdictDeLEcart(ecart, marge) };
};

/**
 * Les domaines des évaluations nationales : le type_label d'une question du QCM de suivi, et la clé du test dans
 * la copie de septembre. La compréhension sur une forme parallèle (d'autres textes) n'a pas d'item de septembre :
 * ses questions ne trouvent pas de réponse de septembre et se lisent par compétence (progressionParCompetence).
 */
export const DOMAINES = [
    { cle: 'automatismes', nom: 'Automatismes', duType: (tl) => tl === 'Automatisme', cleSeptembre: () => 'Automatismes' },
    { cle: 'problemes', nom: 'Résolution de problèmes', duType: (tl) => tl === 'Résolution de problèmes', cleSeptembre: () => 'Problèmes' },
    { cle: 'lexique', nom: 'Lexique', duType: (tl) => tl === 'Lexique', cleSeptembre: () => 'Lexique' },
    { cle: 'competences', nom: 'Compétences langagières', duType: (tl) => tl === 'Compétences langagières et linguistiques', cleSeptembre: () => 'Compétence' },
    { cle: 'comprehension', nom: "Compréhension de l'écrit", duType: (tl) => tl.startsWith("Compréhension de l'écrit"), cleSeptembre: (tl) => nomDuTexte(tl) },
];

/** Le domaine d'une question (son type_label), ou null. */
export const domaineDuType = (typeLabel) => {
    const tl = sansApostropheCourbe(typeLabel);
    return DOMAINES.find((d) => d.duType(tl)) || null;
};

/** La clé d'un élève : son id, sinon son nom et sa classe. */
const cleEleve = (e) => (e?.id_eleve ? `id:${e.id_eleve}` : `${e?.nom_eleve}|${e?.classe_nom}`);
const aUneCopie = (e) => !!e?.reponses && typeof e.reponses === 'object' && Object.keys(e.reponses).length > 0;

/** Le score de septembre d'un item (0 ou 1), ou null s'il n'est pas dans la copie. */
const scoreSeptembre = (reponses, cleTest, questionNum) => {
    const bloc = reponses?.[cleTest];
    if (!bloc || typeof bloc !== 'object') return null;
    const n = numero(questionNum);
    const cle = Object.keys(bloc).find((k) => numero(k) === n);
    if (cle === undefined) return null;
    return estJuste(bloc[cle]?.score) ? 1 : 0;
};

/** Le score d'une question du QCM de suivi (0 ou 1), ou null si aucune réponse n'est rangée. */
const scoreSuivi = (reponses, question) => {
    const bloc = reponses?.[question.type_label] ?? reponses?.[sansApostropheCourbe(question.type_label)];
    const donnee = bloc?.[String(question.id)];
    if (!donnee || typeof donnee !== 'object') return null;
    return estJuste(donnee.score) ? 1 : 0;
};

/** Les QCM de suivi dans l'ordre où ils ont été passés (date, puis id). */
export const dansLOrdre = (bilans) => [...(bilans || [])].sort((a, b) => {
    const parDate = String(a.date || '').localeCompare(String(b.date || ''));
    return parDate !== 0 ? parDate : (a.id || 0) - (b.id || 0);
});

const cleItem = (domaine, q) => `${domaine.cle}|${domaine.cleSeptembre(sansApostropheCourbe(q.type_label))}|${numero(q.question_num)}`;
const compteVide = () => ({ justes: 0, total: 0 });
const ajouterA = (compte, juste) => { compte.total += 1; compte.justes += juste; };

/** « Auto Q01 », « Lex Q03 », « Arachné Q02 » : le nom court d'un item (texte : le nom du texte en compréhension). */
const COURT = { automatismes: 'Auto', problemes: 'Pb', lexique: 'Lex', competences: 'Comp' };
export const nomCourt = (domaine, texte, num) => `${texte || COURT[domaine.cle] || domaine.nom} Q${String(num).padStart(2, '0')}`;

/**
 * Les items de septembre que reprend un QCM de suivi, élève par élève. Une variante garde l'identité de son item ;
 * deux variantes du même item dans un QCM font UNE colonne (12/10/2026 : sinon la réponse de septembre comptait
 * deux fois). Pour chaque élève qui l'a rendu et chaque item : sept, sa réponse de septembre (null si absente) ;
 * aujs, ses réponses aux variantes d'aujourd'hui ; auj, la PART de ses variantes justes (3 sur 4 : 0,75), null sans
 * réponse ; paires, par domaine, les items répondus des deux côtés. 12/10/2026, simulation de la 401 : « juste
 * seulement si toutes les variantes le sont » montrait 73 % → 50 % sur un item posé quatre fois, alors que 81 % des
 * réponses étaient justes ; un faux recul. Les questions de forme parallèle (d'autres textes) n'ont pas d'item :
 * elles se lisent par compétence.
 */
export const grilleDuBilan = ({ evalNat, evalBilan }) => {
    const itemsSeptembre = new Map();
    (evalNat?.questions || []).forEach((q) => {
        const domaine = domaineDuType(q.type_label);
        if (domaine) itemsSeptembre.set(cleItem(domaine, q), q.nom_label || '');
    });
    const parItem = new Map();
    (evalBilan?.questions || []).forEach((q) => {
        const domaine = domaineDuType(q.type_label);
        if (!domaine) return;
        const cle = cleItem(domaine, q);
        if (!itemsSeptembre.has(cle)) return;
        if (!parItem.has(cle)) {
            parItem.set(cle, {
                cle,
                domaine,
                cleTest: domaine.cleSeptembre(sansApostropheCourbe(q.type_label)),
                num: numero(q.question_num),
                libelle: itemsSeptembre.get(cle) || q.nom_label || '',
                questions: [],
            });
        }
        parItem.get(cle).questions.push(q);
    });
    const ordre = (cle) => DOMAINES.findIndex((d) => d.cle === cle);
    const colonnes = [...parItem.values()]
        .map((c) => ({ ...c, questions: [...c.questions].sort((a, b) => a.id - b.id) }))
        .sort((a, b) => ordre(a.domaine.cle) - ordre(b.domaine.cle) || String(a.cleTest).localeCompare(String(b.cleTest)) || a.num - b.num);

    const copiesNat = new Map((evalNat?.eleves_reponses || []).filter(aUneCopie).map((e) => [cleEleve(e), e]));
    const eleves = (evalBilan?.eleves_reponses || []).filter(aUneCopie).map((e) => {
        const nat = copiesNat.get(cleEleve(e));
        const cases = new Map();
        const paires = new Map();
        const aujourdhui = compteVide();
        colonnes.forEach((c) => {
            const aujs = c.questions.map((q) => scoreSuivi(e.reponses, q));
            const repondues = aujs.filter((s) => s !== null);
            const auj = repondues.length === 0 ? null : repondues.reduce((n, s) => n + s, 0) / repondues.length;
            const sept = nat ? scoreSeptembre(nat.reponses, c.cleTest, c.num) : null;
            cases.set(c.cle, { sept, auj, aujs });
            if (auj !== null) ajouterA(aujourdhui, auj);
            if (auj === null || sept === null) return;
            if (!paires.has(c.domaine.cle)) paires.set(c.domaine.cle, { avant: compteVide(), apres: compteVide() });
            const p = paires.get(c.domaine.cle);
            ajouterA(p.avant, sept);
            ajouterA(p.apres, auj);
        });
        return { cle: cleEleve(e), nom: e.nom_eleve, classe: e.classe_nom, aSeptembre: Boolean(nat), cases, paires, aujourdhui };
    }).sort((a, b) => String(a.nom).localeCompare(String(b.nom), 'fr'));
    return { colonnes, eleves, nbCopies: (evalBilan?.eleves_reponses || []).length };
};

/** Assez d'élèves aux deux copies pour dire un mot de la classe (en dessous, seulement les taux). */
export const SEUIL_COPIES_CLASSE = 5;

/**
 * La classe par domaine : la moyenne d'avant et celle de maintenant, sur les items de ce QCM et les élèves aux
 * deux copies. Le verdict (progrès, stable, recul) passe par la marge du hasard, qui ne s'affiche pas. Null sans
 * rien à comparer.
 */
export const bilanDeLaClasse = ({ evalNat, evalBilan }) => {
    const { colonnes, eleves } = grilleDuBilan({ evalNat, evalBilan });
    const domaines = DOMAINES.map((domaine) => {
        const avant = compteVide();
        const apres = compteVide();
        let nbEleves = 0;
        eleves.forEach((e) => {
            const p = e.paires.get(domaine.cle);
            if (!p) return;
            nbEleves += 1;
            avant.justes += p.avant.justes; avant.total += p.avant.total;
            apres.justes += p.apres.justes; apres.total += p.apres.total;
        });
        if (avant.total === 0) return null;
        const nbQuestions = colonnes.filter((c) => c.domaine.cle === domaine.cle && eleves.some((e) => {
            const reponse = e.cases.get(c.cle);
            return reponse.sept !== null && reponse.auj !== null;
        })).length;
        return { domaine, nbEleves, nbQuestions, ...comparerComptes(avant, apres) };
    }).filter(Boolean);
    if (domaines.length === 0) return null;
    return { nbEleves: eleves.filter((e) => e.paires.size > 0).length, domaines };
};

/**
 * La compréhension sur d'autres textes qu'en septembre (forme parallèle), par compétence, sur le même type de
 * texte (formeDuBilan) : la moyenne d'avant et celle de maintenant, élèves aux deux copies. Null sans forme posée.
 */
export const comprehensionDeLaClasse = ({ evalNat, evalBilan, forme }) => {
    const formeDuJour = formeDuBilan(forme, evalBilan?.questions);
    if (!formeDuJour) return null;
    const { classe } = progressionComprehension({ evalNat, evalBilan, forme: formeDuJour });
    if (!classe.eleves) return null;
    const competences = COMPETENCES_ORDRE
        .map((cle) => {
            const comparaison = comparerComptes(classe.septembre.parCompetence[cle], classe.bilan.parCompetence[cle]);
            return comparaison ? { cle, nbEleves: classe.eleves, ...comparaison } : null;
        })
        .filter(Boolean);
    if (competences.length === 0) return null;
    return {
        nbEleves: classe.eleves,
        textes: formeDuJour.textes.map((t) => t.SUPPORT.titre),
        supports: [...new Set(formeDuJour.textes.map((t) => t.support))],
        competences,
    };
};

/**
 * La compréhension d'un élève, avant puis maintenant, COMPÉTENCE PAR COMPÉTENCE, sur celles que les deux copies
 * ont (12/10/2026, simulation de la 401 : un QCM des seules inférences comparé aux 21 questions de septembre,
 * toutes compétences mêlées, affichait « 10/21 → 3/8 ▼ −10 pts »). Même plan (le même nombre de questions par
 * compétence des deux côtés) : les comptes, sur ces compétences ; sinon, la moyenne de ses taux par compétence,
 * chacune pesant pareil. Null sans compétence commune.
 */
export const comprehensionComparee = (c) => {
    if (!c?.septembre || !c.aujourdhui) return null;
    const avant = c.septembre.parCompetence;
    const apres = c.aujourdhui.parCompetence;
    const competences = COMPETENCES_ORDRE.filter((k) => avant[k]?.total > 0 && apres[k]?.total > 0);
    if (competences.length === 0) return null;
    const somme = (comptes) => competences.reduce(
        (acc, k) => ({ justes: acc.justes + comptes[k].justes, total: acc.total + comptes[k].total }), compteVide()
    );
    const moyenne = (comptes) => (100 * competences.reduce((s, k) => s + comptes[k].justes / comptes[k].total, 0)) / competences.length;
    const memePlan = competences.every((k) => avant[k].total === apres[k].total);
    return {
        competences,
        memePlan,
        ...(memePlan ? { avant: somme(avant), apres: somme(apres) } : {}),
        tauxAvant: moyenne(avant),
        tauxApres: moyenne(apres),
        nbQuestions: somme(apres).total,
    };
};

/**
 * L'écart d'un élève, pour le ranger : chaque partie (les items de chaque domaine, la compréhension) donne l'écart
 * de son taux de réussite, pondéré par son nombre de questions d'aujourd'hui. Null sans rien à comparer.
 */
const ecartDeLEleve = (e) => {
    let somme = 0;
    let poids = 0;
    e.paires.forEach((p) => {
        somme += p.apres.justes - p.avant.justes;
        poids += p.apres.total;
    });
    const c = comprehensionComparee(e.comprehension);
    if (c) {
        somme += ((c.tauxApres - c.tauxAvant) / 100) * c.nbQuestions;
        poids += c.nbQuestions;
    }
    return poids > 0 ? somme / poids : null;
};

/**
 * Les élèves qui ont rendu ce QCM, du plus grand progrès au plus grand recul (ceux sans rien à comparer à la
 * fin) : leurs items (grilleDuBilan) et, sur une forme parallèle, leur compréhension sur le même type de texte.
 */
export const elevesDuQcm = ({ evalNat, evalBilan, forme = null }) => {
    const { colonnes, eleves } = grilleDuBilan({ evalNat, evalBilan });
    const formeDuJour = formeDuBilan(forme, evalBilan?.questions);
    const comprehension = formeDuJour
        ? new Map(progressionComprehension({ evalNat, evalBilan, forme: formeDuJour }).eleves.map((e) => [`${e.nom}|${e.classe}`, e]))
        : new Map();
    const complets = eleves.map((e) => {
        const c = comprehension.get(`${e.nom}|${e.classe}`);
        return { ...e, comprehension: c && c.bilan ? { septembre: c.septembre, aujourdhui: c.bilan } : null };
    });
    const ecarts = new Map(complets.map((e) => [e.cle, ecartDeLEleve(e)]));
    const tries = [...complets].sort((a, b) => {
        const ea = ecarts.get(a.cle);
        const eb = ecarts.get(b.cle);
        if (ea === null || eb === null) return ea === eb ? 0 : (ea === null ? 1 : -1);
        // Deux écarts égaux en calcul exact peuvent différer en flottants (3/7 − 2/7 et 7/7 − 6/7) : l'ordre des noms
        return Math.abs(eb - ea) < 1e-9 ? 0 : eb - ea;
    });
    return { colonnes, eleves: tries };
};

/**
 * Le statut d'une question pour un élève, d'après ses essais dans l'ordre (septembre compris) : { juste, qcm }.
 * À un QCM à choix, une réussite seule peut venir du hasard (une fois sur quatre) : il en faut deux de suite ;
 * pour une réponse à taper ou à construire, une suffit.
 */
export const STATUTS = ['a_revoir', 'fragile', 'a_confirmer', 'acquis'];
export const statutDesEssais = (essais) => {
    if (!essais || essais.length === 0) return null;
    const dernier = essais[essais.length - 1];
    if (dernier.juste) {
        if (!dernier.qcm) return 'acquis';
        const avant = essais[essais.length - 2];
        return avant && avant.juste ? 'acquis' : 'a_confirmer';
    }
    return essais.slice(0, -1).some((e) => e.juste) ? 'fragile' : 'a_revoir';
};

const estQcm = (typeQuestion) => !typeQuestion || typeQuestion === 'qcm';

/**
 * Étape 2 : les questions repassées dans au moins un QCM de suivi. Pour chacune, les essais de chaque élève
 * (septembre, puis chaque QCM dans l'ordre), son statut, et les élèves de chaque statut. Les questions de la
 * compréhension sur une forme parallèle n'ont pas d'item de septembre : elles n'y sont pas.
 */
export const questionsRepassees = ({ evalNat, bilans }) => {
    const libelles = new Map();
    const formats = new Map();
    (evalNat?.questions || []).forEach((q) => {
        const domaine = domaineDuType(q.type_label);
        if (!domaine) return;
        const cle = cleItem(domaine, q);
        libelles.set(cle, q.nom_label || '');
        formats.set(cle, q.type_question);
    });
    const copiesNat = new Map((evalNat?.eleves_reponses || []).filter(aUneCopie).map((e) => [cleEleve(e), e]));
    const items = new Map();
    const itemDe = (domaine, cleTest, num, typeLabel) => {
        const cle = `${domaine.cle}|${cleTest}|${numero(num)}`;
        if (!items.has(cle)) items.set(cle, { cle, domaine, cleTest, num: numero(num), typeLabel, qcmIds: new Set(), essais: new Map(), noms: new Map() });
        return items.get(cle);
    };

    dansLOrdre(bilans).forEach((bilan) => {
        (bilan.questions || []).forEach((q) => {
            const domaine = domaineDuType(q.type_label);
            if (!domaine) return;
            const cleTest = domaine.cleSeptembre(sansApostropheCourbe(q.type_label));
            const item = itemDe(domaine, cleTest, q.question_num, q.type_label);
            (bilan.eleves_reponses || []).filter(aUneCopie).forEach((e) => {
                const suivi = scoreSuivi(e.reponses, q);
                if (suivi === null) return;
                const eleve = cleEleve(e);
                item.qcmIds.add(bilan.id);
                item.noms.set(eleve, e.nom_eleve);
                if (!item.essais.has(eleve)) {
                    // Le premier essai : la copie de septembre, si l'item y est
                    const sept = scoreSeptembre(copiesNat.get(eleve)?.reponses, cleTest, q.question_num);
                    item.essais.set(eleve, sept === null ? [] : [{ juste: sept === 1, qcm: estQcm(formats.get(item.cle)), septembre: true }]);
                }
                item.essais.get(eleve).push({ juste: suivi === 1, qcm: estQcm(q.type_question), bilan: bilan.id });
            });
        });
    });

    return [...items.values()]
        // Seulement les items de septembre (une question de forme parallèle n'en a pas)
        .filter((item) => libelles.has(item.cle) && item.qcmIds.size > 0 && item.essais.size > 0)
        .map((item) => {
            const parStatut = Object.fromEntries(STATUTS.map((s) => [s, []]));
            item.essais.forEach((essais, eleve) => {
                const statut = statutDesEssais(essais);
                if (statut) parStatut[statut].push(item.noms.get(eleve));
            });
            STATUTS.forEach((s) => parStatut[s].sort((a, b) => String(a).localeCompare(String(b), 'fr')));
            return {
                cle: item.cle,
                domaine: item.domaine,
                texte: item.domaine.cle === 'comprehension' ? item.cleTest : null,
                num: item.num,
                libelle: libelles.get(item.cle) || '',
                nbQcm: item.qcmIds.size,
                parStatut,
                aReprendre: parStatut.a_revoir.length + parStatut.fragile.length,
            };
        })
        .sort((a, b) => b.aReprendre - a.aReprendre || a.domaine.nom.localeCompare(b.domaine.nom, 'fr') || a.num - b.num);
};
